import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import { createHeadWarp, type HeadWarp } from './headWarp.js';
import type { UserCue } from './userCues.js';
import type { Track } from './useVoice.js';
import { JAW, REST_NARROW, type VisemeCue } from './visemes.js';

/**
 * Avatar fotografico.
 *
 * El parecido no se dibuja: es la fotografia real del asesor. Al hablar se
 * mueven la mandibula, las comisuras, el labio superior, los parpados y la
 * cabeza, y todo ello con pixeles de la propia fotografia. No se pinta nada.
 *
 * Todo lo pesado esta horneado de antemano (`tools/bake_avatar.py`): aqui se
 * hacen unos pocos drawImage por fotograma. Va sobrado en un movil modesto.
 *
 * Las constantes de movimiento no son de oido: estan medidas sobre un video
 * real del propio asesor hablando (`docs/AVATAR.md`, apartado 9).
 */

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Lips {
  cx: number;
  rx: number;
  lipY: number;
  apexY: number;
  cornerRx: number;
  cornerRy: number;
  roundShift: number;
  lipDrop: number;
  bandTop: number;
}

interface Rig {
  version: string;
  width: number;
  height: number;
  iod: number;
  restDrop: number;
  closedDrop: number;
  /** Escala fuente->lamina y hueco entre labios a desplazamiento cero (px fuente). */
  scale?: number;
  gapAtZero?: number;
  /** Fondo fijo opcional (nombre de archivo en /avatar/). */
  bg?: string;
  /** Desplazamiento a partir del cual los labios se separan (medido). */
  sepDrop?: number;
  /** Borde inferior del labio superior, [x, y] cada pocos pixeles. */
  lipEdge?: [number, number][];
  openDrop: number;
  drops: number[];
  restIndex: number;
  blinks: number;
  /** Niveles de cejas en eyes.webp (indice = ceja * blinks + parpadeo). */
  browLevels?: number;
  /** Miradas horneadas en gaze.webp, en orden; 0 es la base. */
  gazeLevels?: number[];
  mouth: Rect;
  eyes: Rect;
  lips: Lips;
  /** Mapa de profundidad para el giro 2.5D (tools/bake_depth.py). */
  depth?: { file: string; max: number };
}

export interface PhotoAvatarProps {
  trackRef: MutableRefObject<Track | null>;
  ampRef: MutableRefObject<number>;
  speaking?: boolean;
  /** Hay una consulta en curso: el avatar escucha y piensa (mira a un lado, ceja atenta). */
  attentive?: boolean;
  /** El usuario esta hablando al microfono: el avatar escucha (mira a camara, asiente). */
  listening?: boolean;
  /** Lo que la camara ve del usuario (si la activo): posicion, sonrisa, gestos. */
  userRef?: MutableRefObject<UserCue | null>;
  onUnavailable?: () => void;
  className?: string;
}

/**
 * Mezcla por dominancia (ver JAW en visemes.ts). Se pregunta que quieren hacer
 * con la boca TODOS los fonemas cercanos, cada uno con su fuerza, y se hace la
 * media ponderada. De ahi salen solas la co-articulacion, el infraalcance al
 * hablar rapido y la anticipacion del sonido siguiente.
 */
const THETA = 0.018;
const WINDOW = 350;
/** La boca va un poco por delante del sonido: se tolera peor una boca tardia. */
const LEAD_MS = 70;

/** Normal estandar por Box-Muller. */
const gauss = () => {
  let u = 0;
  let v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
};

/**
 * Seguidor de segundo orden criticamente amortiguado, en forma cerrada.
 * Estable a cualquier dt y arranca con velocidad cero: un primer orden tiene su
 * velocidad maxima en el instante cero y eso se ve como un tiron.
 */
function follow(x: number, v: number, target: number, w: number, dt: number) {
  const e = Math.exp(-w * dt);
  const dx = x - target;
  const c = v + w * dx;
  return [target + (dx + c * dt) * e, (v - w * c * dt) * e] as const;
}

/**
 * Deriva aleatoria con vuelta al centro (Ornstein-Uhlenbeck, amortiguada).
 * Es lo que hace que la cabeza se mueva sin repetirse y sin irse.
 */
function drift(x: number, v: number, theta: number, sigma: number, dt: number) {
  const nv = v + (-theta * x - 2 * Math.sqrt(theta) * v) * dt + sigma * gauss() * Math.sqrt(dt);
  return [x + nv * dt, nv] as const;
}

export function PhotoAvatar({
  trackRef,
  ampRef,
  speaking = false,
  attentive = false,
  listening = false,
  userRef,
  onUnavailable,
  className,
}: PhotoAvatarProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [kit, setKit] = useState<{
    base: HTMLImageElement;
    mouth: HTMLImageElement;
    eyes: HTMLImageElement;
    /** Miradas (solo el interior de los ojos), si la lamina las trae. */
    gaze: HTMLImageElement | null;
    /** Fondo fijo, si la lamina lo trae: la cabeza se mueve sobre el. */
    bg: HTMLImageElement | null;
    /** Profundidad para girar la cabeza, si la lamina la trae. */
    depth: HTMLImageElement | null;
    rig: Rig;
  } | null>(null);

  const live = useRef({ speaking, attentive, listening });
  live.current = { speaking, attentive, listening };
  // Por referencia, NO como dependencia del efecto: App la recrea en cada
  // render y, si el efecto dependiera de ella, recargaria los activos y
  // reiniciaria la animacion con cada fonema y cada token recibido.
  const unavailableRef = useRef(onUnavailable);
  unavailableRef.current = onUnavailable;

  useEffect(() => {
    let cancelled = false;
    const load = (src: string) =>
      new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(src));
        img.src = src;
      });

    // La geometria se lee sin cache y las tiras se piden SELLADAS con su
    // huella, para que una tira antigua nunca pueda combinarse con una
    // geometria nueva. Y se comprueba que cada imagen mide lo que la geometria
    // dice: antes que dibujar un destrozo, se cae a la ilustracion.
    void fetch('/avatar/rig.json', { cache: 'no-store' })
      .then((r) => r.json() as Promise<Rig>)
      .then(async (rig) => {
        const v = rig.version ? `?v=${rig.version}` : '';
        const [base, mouth, eyes, bg, gaze, depth] = await Promise.all([
          load(`/avatar/base.webp${v}`),
          load(`/avatar/mouth.webp${v}`),
          load(`/avatar/eyes.webp${v}`),
          rig.bg ? load(`/avatar/${rig.bg}${v}`) : Promise.resolve(null),
          rig.gazeLevels?.length ? load(`/avatar/gaze.webp${v}`) : Promise.resolve(null),
          // Sin profundidad el avatar funciona igual, solo que sin girar.
          rig.depth ? load(`/avatar/${rig.depth.file}${v}`).catch(() => null) : Promise.resolve(null),
        ]);
        const ok =
          mouth.naturalWidth === rig.mouth.w &&
          mouth.naturalHeight === rig.mouth.h * rig.drops.length &&
          eyes.naturalWidth === rig.eyes.w &&
          eyes.naturalHeight === rig.eyes.h * rig.blinks * (rig.browLevels ?? 1) &&
          (!gaze || gaze.naturalHeight === rig.eyes.h * (rig.gazeLevels?.length ?? 0)) &&
          base.naturalWidth === rig.width &&
          base.naturalHeight === rig.height &&
          (!bg || (bg.naturalWidth === rig.width && bg.naturalHeight === rig.height)) &&
          !!rig.lips;
        if (!ok) throw new Error('activos del avatar descuadrados con rig.json');
        const depthOk = depth && depth.naturalWidth === rig.width && depth.naturalHeight === rig.height ? depth : null;
        if (!cancelled) setKit({ base, mouth, eyes, gaze, bg, depth: depthOk, rig });
      })
      .catch((error: unknown) => {
        console.warn('[avatar] se usa la ilustracion:', error);
        if (!cancelled) unavailableRef.current?.();
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !kit) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { base, mouth, eyes, gaze, bg, depth, rig } = kit;
    const L = rig.lips;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = rig.width * dpr;
    canvas.height = rig.height * dpr;

    // La cara se compone en un lienzo aparte, SIN transformacion, porque las
    // copias de comisuras y labio leen pixeles del propio lienzo y eso solo
    // funciona en coordenadas de pantalla. Luego se pinta entera con el
    // cabeceo aplicado.
    const face = document.createElement('canvas');
    face.width = rig.width;
    face.height = rig.height;
    const fctx = face.getContext('2d')!;

    // --- Regiones del redondeo y sus mascaras, calculadas una sola vez -------
    // Cada region se ESTIRA anclada en su borde exterior, no se traslada: asi
    // el desplazamiento es cero justo donde la mascara se desvanece, y la
    // mezcla es de un pixel consigo mismo. Trasladar duplicaria el bigote con
    // unos pixeles de desfase, que es el fantasma que mas delata un montaje.
    const maxDropPx = rig.openDrop - rig.restDrop;
    const sepDrop = rig.sepDrop ?? rig.restDrop + 0.25 * (rig.openDrop - rig.restDrop);
    // Borde inferior del labio superior por columna (medido al hornear). Si
    // un rig antiguo no lo trae, se usa una parabola con el apice en el centro.
    const lipEdgeAt = (x: number) => {
      const e = rig.lipEdge;
      if (!e || e.length < 2) return L.apexY - 6 * ((x - L.cx) / L.rx) ** 2;
      if (x <= e[0]![0]) return e[0]![1];
      for (let k = 1; k < e.length; k += 1) {
        if (x <= e[k]![0]) {
          const [x0, y0] = e[k - 1]!;
          const [x1, y1] = e[k]!;
          return y0 + ((y1 - y0) * (x - x0)) / Math.max(1, x1 - x0);
        }
      }
      return e[e.length - 1]![1];
    };
    const region = (w: number, h: number) => {
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      return c;
    };

    // Region del estrechamiento: un rectangulo alrededor de la boca que se
    // deforma como un todo, fila a fila, con desplazamiento CERO en sus cuatro
    // bordes. No lleva mascara: no hay nada que fundir. Las comisuras entran,
    // el interior se comprime y la piel exterior se estira anclada lejos, en
    // la mejilla, como hace la piel de verdad al fruncir.
    const warpX0 = Math.max(0, Math.round(L.cx - L.rx - 0.9 * rig.iod));
    const warpX1 = Math.min(rig.width, Math.round(L.cx + L.rx + 0.9 * rig.iod));
    const warpY0 = Math.max(0, Math.round(L.lipY - 0.45 * rig.iod));
    const warpY1 = Math.min(rig.height, Math.round(L.lipY + 0.45 * rig.iod + maxDropPx * 0.5));
    const warpW = warpX1 - warpX0;
    const warpH = warpY1 - warpY0;
    const warpSrc = region(warpW, warpH);
    const warpSig = 0.26 * rig.iod;

    const bandW = L.rx * 2 + 36;
    let bandBottom = L.apexY;
    for (let x = 0; x < bandW; x += 1) bandBottom = Math.max(bandBottom, lipEdgeAt(L.cx - bandW / 2 + x));
    const bandH = Math.ceil(bandBottom) + 6 - L.bandTop;
    const bandX = L.cx - bandW / 2;
    const band = region(bandW, bandH);
    /** Peso horizontal de la banda: coseno alzado, pleno en el centro y nulo en las comisuras. */
    const bandWeight = (u: number) => 0.5 * (1 - Math.cos(2 * Math.PI * Math.min(1, Math.max(0, u))));

    /**
     * Mascara del labio superior: fundida en los lados y cortada justo por
     * debajo del borde del labio, columna a columna. Asi la banda solo lleva
     * bigote y labio: al estirarla hacia abajo el labio TAPA los dientes, que
     * no se mueven, en vez de arrastrarlos consigo (eso dejaba una linea
     * recta a traves de la boca).
     */
    const bandMask = (() => {
      const m = region(bandW, bandH);
      const g = m.getContext('2d')!;
      const img = g.createImageData(bandW, bandH);
      for (let x = 0; x < bandW; x += 1) {
        const hor = Math.min(1, bandWeight(x / bandW) * 1.25);
        const edge = lipEdgeAt(L.cx - bandW / 2 + x) + 1.5 - L.bandTop;
        for (let y = 0; y < bandH; y += 1) {
          const ver = Math.min(1, Math.max(0, (edge - y) / 3 + 1));
          img.data[(y * bandW + x) * 4 + 3] = Math.round(255 * hor * ver);
        }
      }
      g.putImageData(img, 0, 0);
      return m;
    })();

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Giro 2.5D de la cabeza (WebGL). Sin WebGL o sin mapa, sin giro.
    let warp: HeadWarp | null = null;
    if (depth && rig.depth && !reduced) {
      try {
        warp = createHeadWarp(rig.width, rig.height, depth, rig.depth.max);
      } catch {
        warp = null;
      }
    }

    // --- Estado del movimiento ----------------------------------------------
    let open = 0;
    let vOpen = 0;
    let round = 0;
    let narrow = REST_NARROW;
    // Vida: cejas, mirada, gestos y respiracion. Son lo que separa una cara
    // que habla de un video en bucle: nada de esto se repite igual dos veces.
    let brow = 0;
    let vBrow = 0;
    let browPulse = 0;                  // levantamiento breve (enfasis, arranque de frase)
    let gazeLevel = 0;                  // mirada actual, uno de los niveles horneados o 0
    let nextGaze = 1.5 + Math.random() * 2;
    let gazeHold = 0;
    let beat = 0;                       // asentimiento breve, 0..1
    let beatT = 1;                      // tiempo desde el ultimo asentimiento, s
    let smile = 0;                      // sonrisa: relaja la estrechez de reposo
    let smileT = 99;                    // tiempo desde que empezo
    let nextSmile = 12 + Math.random() * 10;
    let lastGen = -1;
    let wasAttentive = false;
    let nextListenNod = 1.2;
    let vNarrow = 0;
    let vRound = 0;
    let gate = 0;
    let env = 0;
    let cursor = 0;
    let gen = -1;
    let last = performance.now();
    let frame = 0;

    // Giro de la cabeza (grados) y hacia donde quiere mirar. La mirada va
    // primero (sacada) y la cabeza la sigue con un muelle, como en una persona.
    let yaw = 0;
    let vYaw = 0;
    let pitch = 0;
    let vPitch = 0;
    let roll = 0;
    let vRoll = 0;
    let yawT = 0;
    let pitchT = 0;
    let rollT = 0;
    let nextLook = 1.2 + Math.random() * 1.5;
    let thinkSide = 0;
    let listenTilt = 0;
    // Asentimiento de verdad (la cara baja en perspectiva), no solo desplazamiento.
    let nodPhase = 1;
    let nodAmp = 0;
    let prevBeat = 0;
    let lastUserNod = 0;
    let lastUserWave = 0;
    let lastUserSmile = 0;

    // Cabeza: deriva medida en el video del asesor. Rango ±9-10 % de la
    // distancia entre ojos, con el cabeceo vertical dominante.
    let hx = 0;
    let hvx = 0;
    let hy = 0;
    let hvy = 0;
    let hr = 0;
    let hvr = 0;

    // Parpadeo (ver docs/AVATAR.md): lognormal, cierre rapido, apertura lenta.
    let blink = 0;
    let phase: 'idle' | 'down' | 'hold' | 'up' = 'idle';
    let bt = 0;
    let bAmp = 1;
    let bDown = 0.085;
    let bHold = 0.03;
    let bUp = 0.2;
    let pending = 0;
    let nextBlink = 0.9 + Math.random() * 1.8;
    let wasSpeaking = false;

    const scheduleBlink = (sp: boolean) => {
      const mu = sp ? 0.686 : 1.11;
      nextBlink = Math.min(15, Math.max(0.4, Math.exp(mu + 0.55 * gauss())));
    };
    const startBlink = () => {
      phase = 'down';
      bt = 0;
      bAmp = Math.min(1, Math.max(0.55, 1 + 0.18 * gauss()));
      bDown = 0.075 + 0.02 * Math.random();
      bHold = 0.01 + 0.04 * Math.random();
      bUp = 0.15 + 0.12 * Math.random();
    };

    const blendTarget = (cues: VisemeCue[], tMs: number): readonly [number, number, number] => {
      const t = tMs + LEAD_MS;
      let nT = 0;
      let nR = 0;
      let nW = 0;
      let den = 0;
      let denR = 0;
      for (let k = cursor; k < cues.length; k += 1) {
        const c = cues[k]!;
        const d = c.at + c.duration * 0.5 - t;
        if (d < -WINDOW) {
          cursor = k + 1;
          continue;
        }
        if (d > WINDOW) break;
        const j = JAW[c.id];
        const g = j.a * Math.exp(-THETA * (d < 0 ? -d : d));
        nT += g * j.T;
        nW += g * j.W;
        den += g;
        // El redondeo pesa en su propio canal: una /o/ entre dos consonantes
        // planas sigue siendo redonda (medido: su anchura cae al 40-70 %).
        const gr = g * (1 + 1.5 * j.R);
        nR += gr * j.R;
        denR += gr;
      }
      return den > 1e-6 ? [nT / den, nR / Math.max(denR, 1e-6), nW / den] : [0, 0, REST_NARROW];
    };

    const draw = (now: number) => {
      frame = requestAnimationFrame(draw);
      const sp = live.current.speaking;
      const dt = Math.min(0.05, Math.max(0, (now - last) / 1000));
      last = now;

      // --- Objetivos de boca --------------------------------------------------
      const track = trackRef.current;
      if (track && track.gen !== gen) {
        gen = track.gen;
        cursor = 0;
      }
      const [rawT, rawR, rawW] = track ? blendTarget(track.cues, now - track.t0) : [0, 0, REST_NARROW];
      const amp = Math.min(1, Math.max(0, ampRef.current));
      gate += ((sp ? 1 : 0) - gate) * (1 - Math.exp(-dt / 0.12));
      // El volumen escala la apertura, nunca un cierre ni el redondeo.
      let tOpen = rawT > 0 ? rawT * (0.75 + 0.25 * amp) : rawT;
      // Entre frase y frase (la lista de cues se agota antes de que llegue la
      // siguiente) el asesor no cierra la boca del todo: la deja entreabierta.
      // Sin este suelo, cada hueco entre frases era una boca cerrada.
      // Solo cuando la frase ya ha empezado a sonar: `speaking` se activa al
      // encolar, unos 400 ms antes del primer sonido, y abrir ahi la boca se
      // veia como "abre y espera".
      if (sp && tOpen >= 0 && track && now - track.t0 >= 0) tOpen = Math.max(tOpen, 0.2);
      tOpen *= gate;
      const tRound = Math.max(0, rawR) * gate;
      // Callado, la boca esta en su estrechez de reposo, no en la sonrisa de la foto.
      // Sonrisa: al terminar de hablar (y de vez en cuando callado) se relaja
      // la estrechez de reposo hacia la sonrisa de la foto, y vuelve.
      const smileRest = REST_NARROW - 0.6 * smile;
      const tNarrow = rawW * gate + smileRest * (1 - gate);

      [open, vOpen] = follow(open, vOpen, tOpen, 72, dt);
      [round, vRound] = follow(round, vRound, tRound, 60, dt);
      [narrow, vNarrow] = follow(narrow, vNarrow, tNarrow, 50, dt);

      // --- Gestos ligados al turno de palabra --------------------------------
      const att = live.current.attentive && !sp;
      const lis = live.current.listening && !sp && !att;
      if (track && track.gen !== lastGen) {
        // Arranque de frase: asentimiento breve y un toque de cejas.
        lastGen = track.gen;
        if (beatT > 0.6) { beat = 1; beatT = 0; }
        browPulse = Math.max(browPulse, 0.8);
        // Y la cabeza cambia de postura con la frase nueva.
        nextLook = Math.min(nextLook, 0.15);
      }
      if (att && !wasAttentive) {
        // Llega una pregunta: asiente ("te escucho") y mira a un lado a pensar.
        beat = 1; beatT = 0;
        gazeLevel = Math.random() < 0.5 ? -1 : 1;
        gazeHold = 9;
      }
      if (!att && wasAttentive) { gazeLevel = 0; gazeHold = 0; }
      wasAttentive = att;
      if (wasSpeaking && !sp) { smile = 1; smileT = 0; nextSmile = 14 + Math.random() * 12; }
      // Enfasis: con el volumen alto, de vez en cuando, una ceja.
      if (sp && env > 0.72 && browPulse < 0.2 && Math.random() < dt * 1.2) browPulse = 0.6 + 0.4 * Math.random();
      // El pulso dura ~0,45 s: lo bastante para que la ceja llegue arriba (el
      // seguidor suaviza) y se lea como gesto, no como un tic.
      browPulse = Math.max(0, browPulse - dt / 0.45);
      // Escuchando: mira a camara, cejas algo levantadas y asiente de vez en
      // cuando ("te sigo"), como hace una persona mientras le hablan.
      if (lis) {
        gazeLevel = 0;
        gazeHold = 0.5;
        nextListenNod -= dt;
        if (nextListenNod <= 0) { beat = 0.7; beatT = 0; nextListenNod = 1.6 + Math.random() * 1.8; }
      }
      const tBrow = att ? 0.55 : lis ? 0.35 : browPulse;
      [brow, vBrow] = follow(brow, vBrow, tBrow, 45, dt);
      brow = Math.min(1, Math.max(0, brow));
      beatT += dt;
      beat = Math.max(0, beat - dt / 0.42);
      smileT += dt;
      if (smile > 0 && smileT > 1.3) smile = Math.max(0, smile - dt / 0.6);
      if (!sp && !att) {
        nextSmile -= dt;
        if (nextSmile <= 0) { smile = 0.7; smileT = 0; nextSmile = 14 + Math.random() * 12; }
      }
      // Sacadas: la mirada salta (no se desliza) a un lado y vuelve. Hablando,
      // mira a la camara casi siempre; callado se distrae un poco mas.
      if (gazeHold > 0) {
        gazeHold -= dt;
        if (gazeHold <= 0 && !att) gazeLevel = 0;
      } else {
        nextGaze -= dt;
        if (nextGaze <= 0) {
          const levels = rig.gazeLevels ?? [];
          if (levels.length && !att) {
            gazeLevel = levels[Math.floor(Math.random() * levels.length)]!;
            gazeHold = 0.35 + Math.random() * (sp ? 0.6 : 1.4);
          }
          nextGaze = sp ? 3 + Math.random() * 5 : 1.8 + Math.random() * 3.5;
        }
      }
      open = Math.min(1.05, Math.max(-0.3, open));
      round = Math.min(1, Math.max(0, round));
      narrow = Math.min(1, Math.max(0, narrow));
      // Diagnostico: permite fijar la boca en una postura para fotografiarla.
      const force = (window as unknown as { __avatarForce?: { open?: number; round?: number; narrow?: number } }).__avatarForce;
      if (force) {
        if (typeof force.open === 'number') open = force.open;
        if (typeof force.round === 'number') round = force.round;
        if (typeof force.narrow === 'number') narrow = force.narrow;
      }

      // --- Giro de cabeza y mirada (2.5D) --------------------------------------
      // Es lo que mas separa a una persona de un video en bucle: nunca mira
      // dos veces al mismo sitio ni con el mismo ritmo, y reacciona a lo que
      // pasa (pensar, escuchar, a la persona que tiene delante).
      if (!reduced) {
        const cue = userRef?.current;
        const user = cue && cue.present && now - cue.t < 900 ? cue : null;
        if (att) {
          // Pensando: arriba y a un lado, con la mirada por delante.
          if (!thinkSide) {
            thinkSide = Math.random() < 0.5 ? -1 : 1;
            gazeLevel = thinkSide;
          }
          yawT = 6.5 * thinkSide;
          pitchT = -3.2;
          rollT = 1.8 * thinkSide;
          gazeHold = 0.6;
        } else {
          if (thinkSide) {
            thinkSide = 0;
            yawT = 0;
            pitchT = 0;
            rollT = 0;
            nextLook = 0.8;
          }
          if (lis) {
            // Escuchando: de frente, la cabeza algo ladeada, atento.
            if (!listenTilt) listenTilt = (Math.random() < 0.5 ? -1 : 1) * (2.5 + Math.random() * 1.5);
            yawT = user ? user.x * 5 : 0;
            pitchT = 1;
            rollT = listenTilt;
          } else {
            listenTilt = 0;
            if (user && !sp) {
              // Hay alguien delante: lo sigue con la cabeza (la mirada ya esta
              // en la camara, que es donde esta la persona).
              yawT = Math.max(-8, Math.min(8, user.x * 9));
              pitchT = Math.max(-4, Math.min(4, user.y * 5));
              rollT = 0;
              nextLook = 1.5 + Math.random() * 2;
            } else {
              nextLook -= dt;
              if (nextLook <= 0) {
                if (sp) {
                  // Hablando: la cabeza acompana la frase, sin irse de la camara.
                  yawT = (Math.random() * 2 - 1) * 3.2;
                  pitchT = (Math.random() * 2 - 1) * 1.6;
                  rollT = (Math.random() * 2 - 1) * 1.4;
                  nextLook = 1.1 + Math.random() * 2.2;
                } else {
                  const r = Math.random();
                  if (r < 0.3 && Math.abs(yawT) > 1.5) {
                    // Vuelve a la camara (solo si estaba mirando a otro lado: si ya
                    // mira de frente, elegir "de frente" otra vez lo dejaria quieto).
                    yawT = 0;
                    pitchT = 0;
                    nextLook = 2.5 + Math.random() * 3.5;
                  } else if (r < 0.5) {
                    // Una mirada mas larga a un lado, con los ojos por delante.
                    const side = Math.random() < 0.5 ? -1 : 1;
                    yawT = side * (5.5 + Math.random() * 2.5);
                    pitchT = (Math.random() * 2 - 1) * 2.5;
                    gazeLevel = side;
                    gazeHold = 0.7 + Math.random() * 0.6;
                    nextLook = 1.4 + Math.random() * 1.6;
                  } else {
                    yawT = (Math.random() * 2 - 1) * 3.8;
                    pitchT = (Math.random() * 2 - 1) * 2.2;
                    nextLook = 2 + Math.random() * 3.5;
                  }
                  rollT = (Math.random() * 2 - 1) * 1.6;
                }
              }
            }
          }
        }

        // Gestos de la persona (camara): le devuelve la sonrisa, el
        // asentimiento y el saludo.
        if (user) {
          if (user.nods !== lastUserNod) {
            lastUserNod = user.nods;
            if (!sp) { nodAmp = 3.2; nodPhase = 0; }
          }
          if (user.smile > 0.55 && !sp && now - lastUserSmile > 4000) {
            lastUserSmile = now;
            smile = 1;
            smileT = 0;
          }
          if (user.waves !== lastUserWave) {
            lastUserWave = user.waves;
            smile = 1;
            smileT = 0;
            browPulse = 1;
            nodAmp = 3;
            nodPhase = 0;
          }
        } else {
          lastUserNod = cue?.nods ?? lastUserNod;
          lastUserWave = cue?.waves ?? lastUserWave;
        }

        // Cada asentimiento breve (arranque de frase, "te escucho") baja
        // tambien la cara en perspectiva; y el enfasis de la voz, de vez en cuando.
        if (beat > prevBeat + 0.3) { nodAmp = 1.2 + 1.6 * beat; nodPhase = 0; }
        prevBeat = beat;
        if (sp && env > 0.74 && nodPhase >= 1 && Math.random() < dt * 1.4) { nodAmp = 1.8 + Math.random(); nodPhase = 0; }

        [yaw, vYaw] = follow(yaw, vYaw, yawT, sp ? 5.5 : 4.2, dt);
        [pitch, vPitch] = follow(pitch, vPitch, pitchT, 5, dt);
        [roll, vRoll] = follow(roll, vRoll, rollT, 3, dt);
        nodPhase = Math.min(1, nodPhase + dt / 0.5);
      }

      // --- Cabeza -------------------------------------------------------------
      // Deriva con vuelta al centro con la amplitud medida, algo mayor mientras
      // habla, mas un asentimiento ligado al volumen de la voz.
      if (!reduced) {
        // Calibrado por simulacion contra el video del asesor: con theta = 2 y
        // estas sigmas la posicion tiene una desviacion de ~4.5-5 % de la
        // interocular y una velocidad media de ~6 %/s, que es su rango una vez
        // descontado el ruido del seguimiento.
        // Recalibrado con la medida directa (rango ±9 % en 10 s, ~7 %/s):
        // theta 2.6/3.2 y sigma 0.26/0.30 dan ±8.9 % y 7-8 %/s en simulacion.
        const k = 0.7 + 0.5 * gate;
        [hx, hvx] = drift(hx, hvx, 2.6, 0.22 * rig.iod * k, dt);
        [hy, hvy] = drift(hy, hvy, 3.2, 0.3 * rig.iod * k, dt);
        [hr, hvr] = drift(hr, hvr, 1.6, 2.2 * k, dt);
        env += ((sp ? amp : 0) - env) * (1 - Math.exp(-dt / 0.13));
      }

      // --- Parpadeo -----------------------------------------------------------
      if (!reduced) {
        if (wasSpeaking && !sp && phase === 'idle' && Math.random() < 0.72) nextBlink = 0.12;
        wasSpeaking = sp;
        if (phase === 'idle') {
          nextBlink -= dt;
          if (nextBlink <= 0) {
            startBlink();
            if (pending === 0 && Math.random() < 0.17) pending = 1;
          }
        } else {
          bt += dt;
          if (phase === 'down') {
            const u = Math.min(1, bt / bDown);
            blink = bAmp * u * u;
            if (u >= 1) {
              phase = 'hold';
              bt = 0;
            }
          } else if (phase === 'hold') {
            blink = bAmp;
            if (bt >= bHold) {
              phase = 'up';
              bt = 0;
            }
          } else {
            const u = Math.min(1, bt / bUp);
            blink = bAmp * (1 - u * (2 - u));
            if (u >= 1) {
              blink = 0;
              phase = 'idle';
              if (pending > 0) {
                pending -= 1;
                nextBlink = 0.09;
              } else {
                scheduleBlink(sp);
              }
            }
          }
        }
      }

      // ======================================================================
      // Composicion de la cara, sin transformacion
      // ======================================================================
      fctx.clearRect(0, 0, rig.width, rig.height);
      fctx.drawImage(base, 0, 0, rig.width, rig.height);

      // 1. Mandibula: fundido entre los dos fotogramas contiguos.
      // El control `open` es una FRACCION DEL HUECO entre labios, no del
      // recorrido de la mandibula: el hueco es lo que se ve y lo que se midio
      // en el asesor con puntos faciales (mediana 0,108, p90 0,168, maximo
      // 0,236 de la interocular). Un hueco deseado se convierte en
      // desplazamiento con la geometria del propio rig: hueco = (gapAtZero +
      // drop) * scale / iod. Por debajo de sepDrop los labios ya se tocan y
      // solo cambia lo apretados que estan.
      const SEP_K = 0.06;
      const GAP_MIN = 0.0;
      const GAP_MAX = 0.26;
      const scale = rig.scale ?? 1;
      const gap0 = rig.gapAtZero ?? 0;
      let drop: number;
      if (open >= SEP_K) {
        const u = (open - SEP_K) / (1 - SEP_K);
        const gap = GAP_MIN + (GAP_MAX - GAP_MIN) * u;
        drop = Math.max(sepDrop, (gap * rig.iod) / scale - gap0);
      } else if (open >= 0) {
        drop = rig.restDrop + (sepDrop - rig.restDrop) * (open / SEP_K);
      } else {
        drop = rig.restDrop + (rig.restDrop - rig.closedDrop) * (open / 0.16);
      }
      // Al redondear, el labio superior baja y taparia el hueco: la mandibula
      // baja otro tanto, como en una /o/ real, que es abierta ademas de
      // redonda. Sin esto, cada /o/ y /u/ se median como boca casi cerrada.
      if (open > SEP_K) drop += round * L.lipDrop * 0.9;
      drop = Math.min(rig.openDrop, Math.max(rig.closedDrop, drop));
      let lo = 0;
      while (lo < rig.drops.length - 2 && rig.drops[lo + 1]! <= drop) lo += 1;
      const hi = lo + 1;
      const span = rig.drops[hi]! - rig.drops[lo]!;
      const w = span > 1e-6 ? Math.min(1, Math.max(0, (drop - rig.drops[lo]!) / span)) : 0;
      const m = rig.mouth;
      if (lo !== rig.restIndex) {
        fctx.drawImage(mouth, 0, lo * m.h, m.w, m.h, m.x, m.y, m.w, m.h);
      }
      if (w > 0.004 && hi !== rig.restIndex) {
        fctx.globalAlpha = w;
        fctx.drawImage(mouth, 0, hi * m.h, m.w, m.h, m.x, m.y, m.w, m.h);
        fctx.globalAlpha = 1;
      }

      // 2. Anchura y redondeo: comisuras hacia dentro (estrechez, tambien en
      //    reposo) y labio superior hacia abajo (solo en /o/ y /u/).
      if (narrow > 0.02 || round > 0.02) {
        // La foto de origen es una sonrisa: al bajar la mandibula, el labio
        // inferior ensancha la boca (medido: 0,86 -> 0,96 de la interocular).
        // Para que hablando mida lo que el asesor, el estrechamiento crece
        // con la apertura.
        const openFrac = Math.min(1, Math.max(0, (drop - rig.restDrop) / maxDropPx));
        const shift = narrow * L.roundShift * (1 + 0.7 * openFrac);
        const lipDrop = round * L.lipDrop;

        // 2a. Estrechamiento por filas, en tres tramos por fila: [borde,
        //     comisura] se estira, [comisura, comisura] se comprime y el otro
        //     lado se estira. Continuo en x; en y, el desplazamiento sigue una
        //     campana centrada en la boca, y las filas donde es despreciable
        //     ni se tocan. Se lee de una copia: leer y escribir el mismo
        //     lienzo en la misma llamada no esta definido.
        const g = warpSrc.getContext('2d')!;
        g.clearRect(0, 0, warpW, warpH);
        g.drawImage(face, warpX0, warpY0, warpW, warpH, 0, 0, warpW, warpH);
        const cL = L.cx - L.rx - warpX0;
        const cR = L.cx + L.rx - warpX0;
        // La campana se centra entre los dos labios: con la boca abierta, a
        // media altura del hueco, para que ambos labios se estrechen por igual.
        const cy = L.lipY + Math.max(0, drop - rig.restDrop) * 0.5 - warpY0;
        for (let y0 = 0; y0 < warpH; y0 += 2) {
          const h = Math.min(2, warpH - y0);
          const dy = y0 + h / 2 - cy;
          const sh = shift * Math.exp(-(dy * dy) / (2 * warpSig * warpSig));
          if (sh < 0.15) continue;
          const y = warpY0 + y0;
          fctx.drawImage(warpSrc, 0, y0, cL, h, warpX0, y, cL + sh, h);
          fctx.drawImage(warpSrc, cL, y0, cR - cL, h, warpX0 + cL + sh, y, cR - cL - 2 * sh, h);
          fctx.drawImage(warpSrc, cR, y0, warpW - cR, h, warpX0 + cR - sh, y, warpW - cR + sh, h);
        }

        // 2b. Labio superior hacia abajo: se lee DESPUES de estrechar, se
        //     enmascara (solo bigote y labio) y se estira en tiras verticales
        //     con el peso de coseno alzado: el labio baja en el centro y tapa
        //     los dientes, que no se mueven; en las comisuras no pasa nada.
        const bg = band.getContext('2d')!;
        bg.clearRect(0, 0, bandW, bandH);
        bg.drawImage(face, bandX, L.bandTop, bandW, bandH, 0, 0, bandW, bandH);
        bg.globalCompositeOperation = 'destination-in';
        bg.drawImage(bandMask, 0, 0);
        bg.globalCompositeOperation = 'source-over';
        const STRIPS = 16;
        for (let k = 0; k < STRIPS; k += 1) {
          const x0 = Math.floor((k * bandW) / STRIPS);
          const x1 = Math.floor(((k + 1) * bandW) / STRIPS);
          const wgt = bandWeight((x0 + x1) / 2 / bandW);
          fctx.drawImage(band, x0, 0, x1 - x0, bandH, bandX + x0, L.bandTop, x1 - x0, bandH + lipDrop * wgt);
        }
      }

      // 3. Parpados
      {
        const e = rig.eyes;
        const bl = blink > 0.02 ? Math.min(rig.blinks - 1, Math.round(blink * (rig.blinks - 1))) : 0;
        const br = Math.min((rig.browLevels ?? 1) - 1, Math.round(brow * ((rig.browLevels ?? 1) - 1)));
        if (bl > 0 || br > 0) {
          fctx.drawImage(eyes, 0, (br * rig.blinks + bl) * e.h, e.w, e.h, e.x, e.y, e.w, e.h);
        }
        // Mirada: solo con los ojos abiertos (con el parpado bajado no hay interior).
        if (gaze && gazeLevel !== 0 && bl < rig.blinks / 2) {
          const levels = rig.gazeLevels ?? [];
          let k = 0;
          for (let i = 1; i < levels.length; i += 1) if (Math.abs(levels[i]! - gazeLevel) < Math.abs(levels[k]! - gazeLevel)) k = i;
          fctx.drawImage(gaze, 0, k * e.h, e.w, e.h, e.x, e.y, e.w, e.h);
        }
      }

      // ======================================================================
      // Cabeza: la cara compuesta se pinta con el cabeceo aplicado
      // ======================================================================
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, rig.width, rig.height);
      // El fondo no se mueve con la cabeza: si lo hiciera, temblaria la camara.
      if (bg) ctx.drawImage(bg, 0, 0, rig.width, rig.height);
      if (!reduced) {
        // Asentimiento: medio seno, baja y vuelve. Respiracion: 0,5 % de
        // escala en 4,3 s, que en reposo es lo unico que se mueve del torso.
        const nod = env * 0.055 * rig.iod + (beat > 0 ? Math.sin(Math.PI * (1 - beat)) * 0.028 * rig.iod : 0);
        const breath = 1 + 0.005 * Math.sin((2 * Math.PI * now) / 4300);
        const pivotX = rig.width / 2;
        const pivotY = rig.height * 0.78; // gira sobre el cuello, no sobre el centro
        ctx.translate(pivotX + hx, pivotY + hy + nod);
        ctx.rotate(((hr + env * 0.35 + roll) * Math.PI) / 180);
        ctx.scale(breath, breath);
        ctx.translate(-pivotX, -pivotY);
      }
      // Giro 2.5D: la cara compuesta se deforma con su profundidad.
      const force2 = (window as unknown as { __avatarForce?: { yaw?: number; pitch?: number } }).__avatarForce;
      const yawNow = typeof force2?.yaw === 'number' ? force2.yaw : yaw;
      const nodNow = nodPhase < 1 ? Math.sin(Math.PI * nodPhase) * nodAmp : 0;
      const pitchNow = typeof force2?.pitch === 'number' ? force2.pitch : pitch + nodNow;
      if (warp && (Math.abs(yawNow) > 0.05 || Math.abs(pitchNow) > 0.05)) {
        warp.render(face, yawNow, pitchNow);
        ctx.drawImage(warp.canvas, 0, 0, rig.width, rig.height);
      } else {
        ctx.drawImage(face, 0, 0, rig.width, rig.height);
      }
      // Diagnostico: los valores de control en vivo, para poder medir la
      // animacion contra el video de referencia. No tiene coste apreciable.
      (window as unknown as { __avatar?: unknown }).__avatar = { open, round, narrow, brow, gaze: gazeLevel, beat, smile, drop, blink, hx, hy, env, gate, yaw, pitch: pitch + (nodPhase < 1 ? Math.sin(Math.PI * nodPhase) * nodAmp : 0), roll, warp: !!warp, speaking: sp, attentive: att, t: now };
    };

    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [kit]);

  if (!kit) return null;

  return (
    <canvas
      ref={canvasRef}
      className={className}
      role="img"
      aria-label="Asesor academico virtual del Centro de Educacion Continua"
      // El tamano lo decide la hoja de estilos (object-fit: cover en el escenario).
      style={{ display: 'block' }}
    />
  );
}
